import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Switch,
  Divider,
  Alert,
  IconButton,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  PhotoCamera as CameraIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { hasCannotCompleteReason, normalizeChecklistItemOptions } from '../../utils/checklistItemUtils';

interface ChecklistItem {
  id: number;
  title: string;
  description: string;
  item_type: string;
  order: number;
  is_required: boolean;
  validation_rules: any;
  options: string[];
  default_value: string;
  allowed_file_types: string;
  max_file_size_mb: number;
  is_visible: boolean;
  show_when_parent_completed: boolean;
}

interface ItemResponse {
  item_id: number;
  response: any;
  notes: string;
  cannot_complete: boolean;
  cannot_complete_reason: string;
  file_uploads?: File[];
}

interface ChecklistItemRendererProps {
  item: ChecklistItem;
  response?: ItemResponse;
  onResponse: (itemId: number, response: any) => void;
  onNotes: (itemId: number, notes: string) => void;
  onCannotComplete: (itemId: number, cannotComplete: boolean, reason: string) => void;
  isReadOnly?: boolean;
  compact?: boolean;
}

const ChecklistItemRenderer: React.FC<ChecklistItemRendererProps> = ({
  item,
  response,
  onResponse,
  onNotes,
  onCannotComplete,
  isReadOnly = false,
  compact = false,
}) => {
  const [showCannotComplete, setShowCannotComplete] = useState(response?.cannot_complete || false);
  const [cannotCompleteReason, setCannotCompleteReason] = useState(response?.cannot_complete_reason || '');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update local state when response prop changes
  useEffect(() => {
    setShowCannotComplete(response?.cannot_complete || false);
    setCannotCompleteReason(response?.cannot_complete_reason || '');
  }, [response, isReadOnly]);

  const handleCannotCompleteToggle = (checked: boolean) => {
    if (isReadOnly) return; // Prevent changes in read-only mode
    setShowCannotComplete(checked);
    if (checked) {
      onCannotComplete(item.id, true, cannotCompleteReason);
    } else {
      onCannotComplete(item.id, false, '');
      setCannotCompleteReason('');
    }
  };

  const handleCannotCompleteReasonChange = (reason: string) => {
    if (isReadOnly) return; // Prevent changes in read-only mode
    setCannotCompleteReason(reason);
    if (showCannotComplete) {
      onCannotComplete(item.id, true, reason);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isReadOnly) return; // Prevent changes in read-only mode
    const files = event.target.files;
    if (files && files.length > 0) {
      // For file uploads, we need to structure the data properly
      const fileArray = Array.from(files);
      onResponse(item.id, {
        response: fileArray.length > 0 ? true : null, // Mark as completed if files are uploaded
        file_uploads: fileArray
      });
    }
  };

  const handleFileRemove = (index: number) => {
    if (isReadOnly) return; // Prevent changes in read-only mode
    if (response?.file_uploads) {
      const newFiles = response.file_uploads.filter((_, i) => i !== index);
      onResponse(item.id, {
        response: newFiles.length > 0 ? true : null,
        file_uploads: newFiles.length > 0 ? newFiles : null
      });
    }
  };

  const itemOptions = normalizeChecklistItemOptions(item.options);

  const parseMultiSelectResponse = (raw: unknown): string[] => {
    if (Array.isArray(raw)) {
      return raw.map((v) => String(v));
    }
    if (typeof raw === 'string' && raw.trim()) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed.map((v) => String(v));
        }
      } catch {
        return raw.split(';').map((v) => v.trim()).filter(Boolean);
      }
    }
    return [];
  };

  const textFieldRows = compact ? 2 : 3;
  const notesFieldRows = compact ? 2 : 3;

  const renderItemContent = () => {
    if (showCannotComplete) {
      return (
        <Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This item has been marked as "Cannot Complete"
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={textFieldRows}
            size={compact ? 'small' : 'medium'}
            label="Reason for not being able to complete"
            value={cannotCompleteReason}
            onChange={(e) => handleCannotCompleteReasonChange(e.target.value)}
            required
            error={!isReadOnly && !hasCannotCompleteReason(cannotCompleteReason)}
            helperText={
              !isReadOnly && !hasCannotCompleteReason(cannotCompleteReason)
                ? 'A reason is required before this item can be submitted.'
                : 'Please explain why this item cannot be completed'
            }
            disabled={isReadOnly}
          />
        </Box>
      );
    }

    switch (item.item_type) {
      case 'checkbox':
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={response?.response === true}
                onChange={(e) => onResponse(item.id, e.target.checked)}
                color="primary"
                disabled={isReadOnly}
              />
            }
            label="Check this box if the condition is met"
          />
        );

      case 'text_input':
        return (
          <TextField
            fullWidth
            multiline
            rows={textFieldRows}
            size={compact ? 'small' : 'medium'}
            label="Enter your response"
            value={response?.response || ''}
            onChange={(e) => onResponse(item.id, e.target.value)}
            placeholder="Type your response here..."
            disabled={isReadOnly}
          />
        );

      case 'number_input':
        return (
          <TextField
            fullWidth
            type="number"
            label="Enter a number"
            value={response?.response || ''}
            onChange={(e) => onResponse(item.id, parseFloat(e.target.value) || null)}
            placeholder="Enter a number"
            inputProps={{
              min: item.validation_rules?.min_value,
              max: item.validation_rules?.max_value,
              step: item.validation_rules?.step || 1
            }}
            disabled={isReadOnly}
          />
        );

      case 'file_upload':
        return (
          <Box>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ mb: 2 }}
              disabled={isReadOnly}
            >
              Upload Files
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={item.allowed_file_types || '*'}
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              disabled={isReadOnly}
            />
            
            {response?.file_uploads && response.file_uploads.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Uploaded Files:
                </Typography>
                {response.file_uploads.map((file, index) => (
                  <Card key={index} sx={{ mb: 1 }}>
                    <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {file.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleFileRemove(index)}
                          color="error"
                          disabled={isReadOnly}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
            
            <Typography variant="caption" color="text.secondary">
              Allowed file types: {item.allowed_file_types || 'All files'} | 
              Max size: {item.max_file_size_mb || 10}MB
            </Typography>
          </Box>
        );

      case 'date_input':
        return (
          <TextField
            fullWidth
            type="date"
            label="Select a date"
            value={response?.response || ''}
            onChange={(e) => onResponse(item.id, e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={isReadOnly}
          />
        );

      case 'time_input':
        return (
          <TextField
            fullWidth
            type="time"
            label="Select a time"
            value={response?.response || ''}
            onChange={(e) => onResponse(item.id, e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={isReadOnly}
          />
        );

      case 'select':
        return (
          <FormControl fullWidth>
            <InputLabel>Select an option</InputLabel>
            <Select
              value={response?.response || ''}
              label="Select an option"
              onChange={(e) => onResponse(item.id, e.target.value)}
              disabled={isReadOnly}
            >
              {itemOptions.map((option, index) => (
                <MenuItem key={index} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'multi_select': {
        const selectedValues = parseMultiSelectResponse(response?.response);
        return (
          <FormControl fullWidth>
            <InputLabel>Select all that apply</InputLabel>
            <Select
              multiple
              value={selectedValues}
              label="Select all that apply"
              onChange={(e) => {
                const value = e.target.value;
                onResponse(
                  item.id,
                  JSON.stringify(typeof value === 'string' ? value.split(',') : value)
                );
              }}
              disabled={isReadOnly}
              renderValue={(selected) => (selected as string[]).join(', ')}
            >
              {itemOptions.map((option, index) => (
                <MenuItem key={index} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }

      case 'radio':
        return (
          <FormControl component="fieldset">
            <Typography variant="subtitle2" gutterBottom>
              Select one option:
            </Typography>
            {itemOptions.map((option, index) => (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    checked={response?.response === option}
                    onChange={(e) => onResponse(item.id, e.target.checked ? option : null)}
                    color="primary"
                    disabled={isReadOnly}
                  />
                }
                label={option}
              />
            ))}
          </FormControl>
        );

      case 'signature':
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Digital signature functionality would be implemented here
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Signature confirmation"
              value={response?.response || ''}
              onChange={(e) => onResponse(item.id, e.target.value)}
              placeholder="Type your name to confirm"
              disabled={isReadOnly}
            />
          </Box>
        );

      case 'location':
        return (
          <Box>
            <Button
              variant="outlined"
              startIcon={<LocationIcon />}
              onClick={() => {
                // Get current location
                if (navigator.geolocation) {
                  navigator.geolocation.getCurrentPosition(
                    (position) => {
                      const location = {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy
                      };
                      onResponse(item.id, location);
                    },
                    (error) => {
                      console.error('Error getting location:', error);
                      onResponse(item.id, null);
                    }
                  );
                } else {
                  onResponse(item.id, null);
                }
              }}
              disabled={isReadOnly}
            >
              Get Current Location
            </Button>
            {response?.response && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Latitude: {response.response.latitude}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Longitude: {response.response.longitude}
                </Typography>
              </Box>
            )}
          </Box>
        );

      case 'photo':
        return (
          <Box>
            <Button
              variant="outlined"
              startIcon={<CameraIcon />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ mb: 2 }}
              disabled={isReadOnly}
            >
              Take Photo
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              disabled={isReadOnly}
            />
            
            {response?.file_uploads && response.file_uploads.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Photos taken:
                </Typography>
                <Grid container spacing={1}>
                  {response.file_uploads.map((file, index) => (
                    <Grid item xs={6} sm={4} key={index}>
                      <Card>
                        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Photo ${index + 1}`}
                            style={{ width: '100%', height: 'auto', borderRadius: 4 }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleFileRemove(index)}
                            color="error"
                            sx={{ position: 'absolute', top: 4, right: 4 }}
                            disabled={isReadOnly}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>
        );

      default:
        return (
          <Typography variant="body2" color="text.secondary">
            Unknown item type: {item.item_type}
          </Typography>
        );
    }
  };

  return (
    <Box>
      {/* Item Header */}
      <Box sx={{ mb: compact ? 1.5 : 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: compact ? 0.5 : 1 }}>
          <Typography variant={compact ? 'subtitle1' : 'h5'} fontWeight="bold">
            {item.title}
          </Typography>
          {item.is_required && (
            <Chip label="Required" size="small" color="error" />
          )}
        </Box>
        
        {item.description && (
          <Typography variant={compact ? 'body2' : 'body1'} color="text.secondary" sx={{ mb: compact ? 1 : 2 }}>
            {item.description}
          </Typography>
        )}

        {/* Cannot Complete Toggle */}
        <Box sx={{ mb: compact ? 1 : 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showCannotComplete}
                onChange={(e) => handleCannotCompleteToggle(e.target.checked)}
                color="warning"
                disabled={isReadOnly}
                size={compact ? 'small' : 'medium'}
              />
            }
            label={<Typography variant={compact ? 'body2' : 'body1'}>Cannot Complete This Item</Typography>}
          />
        </Box>
      </Box>

      {/* Item Content */}
      {renderItemContent()}

      {/* Notes Section */}
      <Divider sx={{ my: compact ? 1.5 : 3 }} />
      <Box>
        <Typography variant={compact ? 'body2' : 'subtitle1'} gutterBottom={!compact}>
          Additional Notes (Optional)
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={notesFieldRows}
          size={compact ? 'small' : 'medium'}
          label="Add any additional notes or comments"
          value={response?.notes || ''}
          onChange={(e) => onNotes(item.id, e.target.value)}
          placeholder="Enter any additional notes..."
          disabled={isReadOnly}
        />
      </Box>
    </Box>
  );
};

export default ChecklistItemRenderer; 